import {
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  RectangleStackIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline'
import type { SecureScoreByCategory, CategoryScore } from '@/types'

interface CategoryBreakdownProps {
  breakdown: SecureScoreByCategory
}

const categoryConfig: Record<
  string,
  { icon: typeof ShieldCheckIcon; tint: string; tintBg: string }
> = {
  identity: { icon: ShieldCheckIcon, tint: '#4F86A8', tintBg: 'rgba(167,211,240,0.18)' },
  device: { icon: DevicePhoneMobileIcon, tint: '#C9AD80', tintBg: 'rgba(201,173,128,0.18)' },
  apps: { icon: RectangleStackIcon, tint: '#A88556', tintBg: 'rgba(168,133,86,0.14)' },
  data: { icon: CircleStackIcon, tint: '#4A9B6E', tintBg: 'rgba(118,214,157,0.18)' },
}

function editorialScoreColor(percentage: number): string {
  if (percentage >= 80) return '#4A9B6E' // sage
  if (percentage >= 60) return '#4F86A8' // blue
  if (percentage >= 40) return '#C9AD80' // warm sand
  if (percentage >= 20) return '#A88556' // deeper warm
  return '#C84B4B' // severity red
}

export function CategoryBreakdown({ breakdown }: CategoryBreakdownProps) {
  const categories = [
    { key: 'identity', data: breakdown.identity },
    { key: 'device', data: breakdown.device },
    { key: 'apps', data: breakdown.apps },
    { key: 'data', data: breakdown.data },
  ]

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {categories.map(({ key, data }) => (
        <CategoryCard key={key} category={key} score={data} />
      ))}
    </div>
  )
}

interface CategoryCardProps {
  category: string
  score: CategoryScore
}

function CategoryCard({ category, score }: CategoryCardProps) {
  const config = categoryConfig[category]
  const Icon = config?.icon ?? ShieldCheckIcon
  const tint = config?.tint ?? 'var(--color-mute,#6B7280)'
  const tintBg = config?.tintBg ?? 'rgba(107,114,128,0.12)'
  const scoreColor = editorialScoreColor(score.percentage)

  return (
    <div className="flex h-full flex-col rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-5 transition-shadow duration-200 hover:shadow-md">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
          {score.category}
        </span>
        <div
          className="flex h-8 w-8 items-center justify-center rounded-[4px]"
          style={{ background: tintBg }}
        >
          <Icon className="h-4 w-4" style={{ color: tint }} />
        </div>
      </div>

      <span
        className="font-display text-3xl font-extrabold italic tabular-nums leading-none tracking-[-0.02em]"
        style={{ color: scoreColor }}
      >
        {Math.round(score.percentage)}%
      </span>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[color:var(--color-cream,#FBF8F2)]">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(score.percentage, 100)}%`,
            background: scoreColor,
          }}
        />
      </div>

      <div className="mt-3 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.12em] tabular-nums text-[color:var(--color-mute,#6B7280)]">
        <span>
          {Math.round(score.currentScore)}/{Math.round(score.maxScore)} pts
        </span>
        {score.recommendationCount > 0 && (
          <span>
            {score.recommendationCount} rec{score.recommendationCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  )
}
